import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import { createPatient } from "../../../api/services/api";

interface CreatePatientDialogProps {
  open: boolean;
  onClose: () => void;
}

const emptyForm = {
  fullName: "",
  email: "",
  mobileNumber: "",
  gender: "",
  age: "",
  dob: "",
  sendInvite: true,
};

function CreatePatientDialog({ open, onClose }: CreatePatientDialogProps) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({ ...emptyForm });

  const reset = () => setFormData({ ...emptyForm });

  const createMutation = useMutation((data: any) => createPatient(data), {
    onSuccess: (res: any) => {
      const invited = res?.data?.inviteSent ?? res?.inviteSent;
      toast.success(
        invited
          ? "Patient created and invite email sent."
          : "Patient created successfully."
      );
      queryClient.invalidateQueries(["patients"]);
      reset();
      onClose();
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.message ||
          "Failed to create patient. Please try again."
      );
    },
  });

  const handleChange = (field: string) => (event: any) => {
    setFormData({ ...formData, [field]: event.target.value });
  };

  const handleSubmit = () => {
    if (!formData.fullName.trim()) {
      toast.error("Full name is required");
      return;
    }
    if (!formData.email.trim() && !formData.mobileNumber.trim()) {
      toast.error("Provide at least an email or a mobile number");
      return;
    }

    const body: any = {
      fullName: formData.fullName.trim(),
      sendInvite: formData.sendInvite,
    };
    if (formData.email.trim()) body.email = formData.email.trim();
    if (formData.mobileNumber.trim())
      body.mobileNumber = formData.mobileNumber.trim();
    if (formData.gender) body.gender = formData.gender;
    if (formData.age) body.age = Number(formData.age);
    if (formData.dob) body.dob = formData.dob;

    createMutation.mutate(body);
  };

  const handleClose = () => {
    if (createMutation.isLoading) return;
    reset();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Add Patient</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>
          Pre-register a patient with the details you know. They complete the rest
          (and accept the Terms &amp; Conditions) in the app after signing in with
          the same email or mobile number.
        </DialogContentText>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Full Name"
              value={formData.fullName}
              onChange={handleChange("fullName")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleChange("email")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Mobile Number"
              placeholder="+9665XXXXXXXX"
              helperText="Include country code (E.164) so app sign-in matches this record"
              value={formData.mobileNumber}
              onChange={handleChange("mobileNumber")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Gender</InputLabel>
              <Select
                value={formData.gender}
                label="Gender"
                onChange={handleChange("gender")}
              >
                <MenuItem value="">Not set</MenuItem>
                <MenuItem value="MALE">Male</MenuItem>
                <MenuItem value="FEMALE">Female</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Age"
              type="number"
              value={formData.age}
              onChange={handleChange("age")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date of Birth"
              type="date"
              value={formData.dob}
              onChange={handleChange("dob")}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.sendInvite}
                  onChange={(e) =>
                    setFormData({ ...formData, sendInvite: e.target.checked })
                  }
                />
              }
              label="Send invite email (when an email is provided)"
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isLoading}
        >
          {createMutation.isLoading ? "Creating..." : "Create Patient"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default CreatePatientDialog;
